import { LessonType } from '../../core/types';

/**
 * Doar câmpul de care are nevoie garda de rol — nu tot `NavLinkConfig` din
 * `routes-provider.types.ts`, ca funcția să rămână testabilă cu un obiect minimal (același
 * tipar ca `resolve-post-auth-target.test.ts`).
 */
export interface LessonListingNavLink {
  ALLOWED_ROLES: readonly string[];
}

export interface LessonListingContext {
  PATHS: Record<string, string>;
  PAGES: Record<string, string>;
  /** `NAV_LINKS_V2` din `useRoutesProvider()` — obiect pur, cheia e valoarea din `PAGES`. */
  NAV_LINKS_V2: Record<string, LessonListingNavLink>;
  getUserHomePage: (role?: string) => string;
}

/** Cheia PAGES a listing-ului propriu tipului de lecție — fără nicio verificare de rol. */
function getListingPageKey(lessonType: LessonType | string | undefined, PAGES: Record<string, string>): string {
  switch (lessonType) {
    case LessonType.HOMEWORK:
      return PAGES.HOMEWORK_LISTING;
    case LessonType.MONTHLY_CHALLENGE:
      return PAGES.MONTHLY_CHALLENGES_LISTING;
    case LessonType.PRACTICAL_ACTIVITY:
      return PAGES.PRACTICAL_ACTIVITY_LISTING;
    case LessonType.CLASS_EXERCISE:
      return PAGES.CLASS_EXERCISE_LISTING;
    case LessonType.SCHOOL_CHALLENGE:
      return PAGES.SCHOOL_CHALLENGE_LISTING;
    case LessonType.DAILY_CHALLENGE:
      // Nu există azi o rută live de listing pentru provocarea zilnică (comentată în
      // routes-provider.tsx). Cade pe HOMEWORK_LISTING, nu pe /lectii (DBEST/ADMIN-only).
      return PAGES.HOMEWORK_LISTING;
    case LessonType.LESSON:
    default:
      return PAGES.LESSON_QUIZ_LISTING;
  }
}

/**
 * Mapare tip de lecție + rol -> destinația ✕-ului din LessonTopBar (C1, final-review.md; gardă
 * de rol adăugată la re-review, aceeași sesiune).
 *
 * `PATHS[PAGES.LESSON_QUIZ_LISTING]` (`/lectii`) e DBEST/ADMIN-only — folosit ca implicit în
 * `LessonTopBar` cât timp nu i se dă `onExit`. Homework și Monthly Challenge sunt fluxurile pe
 * care le rulează efectiv un elev pe telefon; ambele ieșeau înainte pe `/lectii`, unde elevul nu
 * are acces — landează pe listing-ul de DBEST/ADMIN și e aruncat afară tăcut la următorul refresh
 * de token (`auth-provider-v2.tsx`), nu imediat.
 *
 * Mapping-ul tip -> listing rezolvă majoritatea cazurilor (`ALLOWED_ROLES` identice pe
 * completion și listing), dar nu pe toate: `LESSON_COMPLETION` permite PROFESOR,
 * `LESSON_QUIZ_LISTING` nu — un profesor care completează un `LESSON` ar lovi din nou o rută
 * neaccesibilă lui. De-asta garda verifică `ALLOWED_ROLES` al listing-ului ales și cade pe
 * `getUserHomePage(role)` (rută mereu accesibilă rolului, sau `/` public dacă rolul lipsește)
 * când listing-ul nu-l include.
 *
 * Funcție pură — nu are nevoie de hook-uri, ca să fie testabilă fără provideri montați
 * (același motiv ca `resolve-post-auth-target.ts`). `NAV_LINKS_V2` e deja un obiect pur expus de
 * `useRoutesProvider()` — nu a fost nevoie de un `isAllowed(pageKey, role)` construit separat.
 */
export function getLessonListingPath(
  lessonType: LessonType | string | undefined,
  role: string | undefined,
  { PATHS, PAGES, NAV_LINKS_V2, getUserHomePage }: LessonListingContext
): string {
  const pageKey = getListingPageKey(lessonType, PAGES);
  const allowedRoles = NAV_LINKS_V2[pageKey]?.ALLOWED_ROLES ?? [];
  const roleAllowed = !!role && allowedRoles.includes(role);
  return roleAllowed ? PATHS[pageKey] : getUserHomePage(role);
}
